// src/components/login/mfa-verify-page.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// `ui/button`, `ui/input` and `ui/alert` are gone; the page is `AuthCard` on
// the shared auth surface, the same one login and signup sit on.
//
// What did NOT move: `authApi.mfaVerify`, the two modes, the digit cleaning,
// the three error branches and every message. Everything above the `return`
// is unchanged.
//
// Two defects the swap removed:
//
//   * The submit greyed itself out through `background: var(--muted)` while
//     staying enabled to a screen reader. `Button` takes `disabled`.
//   * "Use a backup code instead" and "Cancel" were <button>s painted to look
//     like links, with no focus treatment of their own. They are kit controls
//     now and take the one focus ring.
import { useState, FormEvent } from "react";
import { authApi } from "@/api/auth";
import { UpmodeLogo } from "./upmode-logo";
import { ShieldCheck, KeyRound } from "lucide-react";
import {
  AuthCard, Banner, Button, FieldRow, FormStack, Icon, InlineLink,
  BackLink, TextInput,
} from "../kit";

interface MfaVerifyPageProps {
  challengeToken: string;
  username: string;
  onVerifySuccess: (
    accessToken: string,
    payload: {
      username: string;
      is_admin: boolean;
      user_type: string;
      full_name?: string | null;
      backup_code_used: boolean;
      backup_codes_remaining: number;
    },
  ) => void;
  onCancel: () => void;
}

export function MfaVerifyPage({
  challengeToken,
  username,
  onVerifySuccess,
  onCancel,
}: MfaVerifyPageProps) {
  const [mode, setMode]   = useState<"totp" | "backup">("totp");
  const [code, setCode]   = useState("");
  const [errorMsg, setErrorMsg]   = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    const trimmed = code.trim();
    if (mode === "totp") {
      const cleaned = trimmed.replace(/\D/g, "");
      if (cleaned.length !== 6) {
        setErrorMsg("Enter the 6-digit code from your authenticator app.");
        return;
      }
    } else if (trimmed.length < 6) {
      setErrorMsg("Enter your backup code (looks like XXXX-XXXX).");
      return;
    }

    setIsLoading(true);
    try {
      const res = await authApi.mfaVerify(
        challengeToken,
        mode === "totp" ? trimmed.replace(/\D/g, "") : trimmed,
        mode === "backup",
      );

      onVerifySuccess(res.access_token, {
        username:               res.username,
        is_admin:               res.is_admin,
        user_type:              res.user_type || "basic",
        full_name:              res.full_name,
        backup_code_used:       res.backup_code_used,
        backup_codes_remaining: res.backup_codes_remaining,
      });
    } catch (e: any) {
      const msg = String(e?.message || "");
      if (/lock/i.test(msg) || /429/.test(msg)) {
        setErrorMsg("Too many failed attempts. Try again in 15 minutes.");
      } else if (/invalid code/i.test(msg) || /401/.test(msg)) {
        setErrorMsg(
          mode === "totp"
            ? "That code didn't match. Try the latest 6-digit code from your app."
            : "Backup code didn't match. Each code can only be used once.",
        );
      } else {
        setErrorMsg(msg || "Verification failed. Try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="upm-auth-page" data-kit>
      <div className="upm-auth-page__wash" aria-hidden="true" />

      <div className="upm-auth-page__column">
        <div className="upm-auth-page__brand">
          <UpmodeLogo />
        </div>

        <AuthCard
          title="Two-factor authentication"
          description={`Signing in as ${username}`}
          footer={
            <BackLink href="#" onClick={(e) => { e.preventDefault(); onCancel(); }}>
              Cancel
            </BackLink>
          }
        >
          {errorMsg ? <Banner tone="error">{errorMsg}</Banner> : null}

          <form onSubmit={handleSubmit}>
            <FormStack>
              <p className="upm-auth-page__hint">
                <Icon icon={mode === "totp" ? ShieldCheck : KeyRound} size="md" />
                {mode === "totp"
                  ? "Enter the 6-digit code from your authenticator app."
                  : "Enter one of your backup codes (XXXX-XXXX)."}
              </p>

              <FieldRow label={mode === "totp" ? "Authentication code" : "Backup code"} required>
                {({ id }) => (
                  <TextInput
                    id={id}
                    className="upm-auth-page__code"
                    type="text"
                    inputMode={mode === "totp" ? "numeric" : "text"}
                    pattern={mode === "totp" ? "[0-9]*" : undefined}
                    maxLength={mode === "totp" ? 6 : 10}
                    value={code}
                    onChange={(e) => {
                      setCode(mode === "totp" ? e.target.value.replace(/\D/g, "") : e.target.value);
                    }}
                    placeholder={mode === "totp" ? "123456" : "ABCD-1234"}
                    autoFocus
                    autoComplete="one-time-code"
                    state={isLoading ? "disabled" : undefined}
                  />
                )}
              </FieldRow>

              <Button
                type="submit"
                disabled={isLoading || !code.trim()}
                loading={isLoading}
                loadingLabel="Verifying"
                className="upm-auth-page__submit"
              >
                Verify
              </Button>

              <p className="upm-auth-page__legal">
                <InlineLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setMode(mode === "totp" ? "backup" : "totp");
                    setCode("");
                    setErrorMsg("");
                  }}
                >
                  {mode === "totp" ? "Use a backup code instead" : "Use authenticator app instead"}
                </InlineLink>
              </p>
            </FormStack>
          </form>
        </AuthCard>
      </div>
    </div>
  );
}
